import { getInLifeQuotingFieldsData } from '@in-life/utils/getInLifeQuotingFieldsData';
import { getPersonEntityData } from '@in-life/utils/getPersonEntityData';
import { useCallback } from 'react';

import { parseEnergyTrackingContext } from '@features/EnergyTariffForm/energyTrackingContext';
import { BundleBasketFragment } from '@generated/gql/graphql';
import {
  useTrackInLifeEnergyAddedToBasket,
  useTrackInLifeEnergyFaqExpanded,
  useTrackInLifeEnergyFaqsHeaderDisplayedOnce,
  useTrackInLifeEnergySmartMeterPromoDisplayedOnce,
  useTrackInLifeEvBannerDisplayedOnce,
  useTrackInLifeEvLinkClickedOnce,
} from '@tracking/trackingEvents';

/**
 * Tracking for the in-life energy tariff form.
 */
const useInLifeEnergyTracking = () => {
  const trackEvLinkClickedOnce = useTrackInLifeEvLinkClickedOnce();
  const evBannerDisplayedOnce = useTrackInLifeEvBannerDisplayedOnce();
  const trackEnergyAddedToBasketCall = useTrackInLifeEnergyAddedToBasket();
  const smartMeterBannerDisplayedOnce =
    useTrackInLifeEnergySmartMeterPromoDisplayedOnce();
  const trackFaqExpanded = useTrackInLifeEnergyFaqExpanded();
  const faqHeaderDisplayedOnce = useTrackInLifeEnergyFaqsHeaderDisplayedOnce();

  const trackEnergyAddedToBasket = (basket: Nullable<BundleBasketFragment>) => {
    if (!basket) {
      throw new Error('submitEnergy: missing basket response');
    }

    if (!basket.energy) {
      return;
    }

    const parsedEnergyTrackingContext = parseEnergyTrackingContext(
      basket.energy
    );

    trackEnergyAddedToBasketCall(
      {
        fuel_type: parsedEnergyTrackingContext.fuel_type,
        energy_usage_category:
          parsedEnergyTrackingContext.energy_usage_category,
        energy_tariff_type: parsedEnergyTrackingContext.energy_tariff_type,
        custom_electricity_usage_kwh:
          parsedEnergyTrackingContext.custom_electricity_usage_kwh,
        custom_gas_usage_kwh: parsedEnergyTrackingContext.custom_gas_usage_kwh,
        has_econ_seven_meter: parsedEnergyTrackingContext.econ_seven_meter,
        has_prepayment_meter: parsedEnergyTrackingContext.prepayment_meter,
      },
      {
        contexts: [getInLifeQuotingFieldsData(), getPersonEntityData()],
      }
    );
  };

  const trackEvLinkClicked = () => {
    trackEvLinkClickedOnce({}, { contexts: [getInLifeQuotingFieldsData()] });
  };

  const trackEvBannerDisplayed = useCallback(() => {
    evBannerDisplayedOnce({}, { contexts: [getInLifeQuotingFieldsData()] });
  }, [evBannerDisplayedOnce]);

  const trackSmartMeterBannerDisplayed = useCallback(() => {
    smartMeterBannerDisplayedOnce(
      {},
      { contexts: [getInLifeQuotingFieldsData()] }
    );
  }, [smartMeterBannerDisplayedOnce]);

  const trackFaqLinkClicked = useCallback(
    (question: string) => {
      trackFaqExpanded(
        { question },
        { contexts: [getInLifeQuotingFieldsData()] }
      );
    },
    [trackFaqExpanded]
  );

  const trackFaqHeaderDisplayed = useCallback(() => {
    faqHeaderDisplayedOnce({}, { contexts: [getInLifeQuotingFieldsData()] });
  }, [faqHeaderDisplayedOnce]);

  return {
    trackEvBannerDisplayed,
    trackEnergyAddedToBasket,
    trackEvLinkClicked,
    trackSmartMeterBannerDisplayed,
    trackFaqLinkClicked,
    trackFaqHeaderDisplayed,
  };
};

export { useInLifeEnergyTracking };
